import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { AnyExtension } from '@tiptap/core';
import { useSelector } from 'react-redux';
import { Email, type EmailCompositionValue } from '@kula-ai/components';
import { customTemplateSelectProps } from '@/components/shared/customTemplateLabel';
import { isTemplateEdited } from '@/components/shared/email/templateSelection';
import { useAttachmentGuard } from '@/components/shared/email/useAttachmentGuard';
import { uploadUserInlineImage } from '@/api/userImageUploads/useUploadUserInlineImage';
import { useGetAllUsers } from '@/hooks/useGetAllUsers';
import {
  useCcBccUserOptions,
  useSenderAutocomplete,
  asyncSelectProps,
} from '@/api/userSearch/useCcBccUserOptions';
import { useGetDefaultFrom } from '@/hooks/useGetDefaultFrom';
import { selectUsers } from '@/store/users/selectors';
import {
  toSelectOptions,
  toEmailValue,
  fromEmailValue,
  isSameEmailValue,
  resolveSelectableFrom,
  toEditorAttachments,
  toTemplateOptions,
  attachmentKey,
} from './templateEmailValue';
import type {
  TemplateEmailUser,
  TemplateEmailAttachment,
} from './templateEmailValue';
import { hasSurveyPlaceholder } from '@/utils/surveyFormatter';

interface SelectedTemplate {
  id?: string | number;
  subject?: string;
  body?: string;
  name?: string;
  [key: string]: unknown;
}

interface TemplateEmailComposerProps {
  formik?: any;
  // Merge-variable payload, already in the category shape kula's picker expects.
  variables?: Record<string, string[]>;
  // Overrides the sender list — some surfaces restrict who may send.
  defaultAllowedFromUsers?: TemplateEmailUser[];
  // The picked template; its subject and body are applied to the composer.
  selectedTemplate?: SelectedTemplate | null;
  // Supplying these moves the template picker into the email card.
  templates?: SelectedTemplate[];
  onTemplateChange?: (_template: SelectedTemplate | null) => void;
  // Server-side search, so the card's picker reaches beyond the first page.
  loadTemplates?: (_input: string) => Promise<SelectedTemplate[]>;
  // Rows the caller pins to the front and refuses to let the user remove.
  nonRemovableAttachments?: TemplateEmailAttachment[];
  onAttachmentRemove?: (_item: TemplateEmailAttachment) => void;
  enableFontControls?: boolean;
  disabled?: boolean;
  // E-sign puts several composers on one form, so these are parameterised.
  showFrom?: boolean;
  subjectFieldName?: string;
  bodyFieldName?: string;
  hideToolbar?: boolean;
  subjectMaxLength?: number;
  // Overrides individual toolbar features; merged over the defaults.
  toolbarFeatures?: Record<string, boolean>;
  // Repo-local tiptap nodes (e.g. the survey pill).
  extraExtensions?: AnyExtension[];
  // Extra toolbar control; takes the tiptap instance, hence a render function.
  renderToolbarStartSlot?: (_editor: unknown) => ReactNode;
}

const SURVEY_LINK_KEYS = ['survey.link'];
const EMPTY_KEYS: string[] = [];

const templateKey = (template: SelectedTemplate | null | undefined): string =>
  template?.id === undefined || template?.id === null
    ? ''
    : String(template.id);

/**
 * Email composition for the shared template composer, on kula-ai `Email`.
 *
 * Rendered as a FormBuilder custom field so every consuming surface keeps its
 * own Formik shell, submit handler and template picker unchanged.
 *
 * Replaces `useEmailField`, whose `FormEditor` disables tiptap's `hardBreak`
 * and so drops every `<br>` in a stored template body (ENG-3719).
 */
export const TemplateEmailComposer = ({
  formik,
  variables,
  defaultAllowedFromUsers,
  selectedTemplate,
  templates,
  onTemplateChange,
  loadTemplates,
  nonRemovableAttachments = [],
  onAttachmentRemove,
  enableFontControls = false,
  disabled = false,
  showFrom = true,
  subjectFieldName = 'subject',
  bodyFieldName = 'body',
  hideToolbar = false,
  subjectMaxLength,
  toolbarFeatures: toolbarFeatureOverrides,
  extraExtensions,
  renderToolbarStartSlot,
}: TemplateEmailComposerProps) => {
  const { fromUsers } = useGetAllUsers();
  const allUsers = useSelector(selectUsers);
  const { from: defaultFrom } = useGetDefaultFrom({});
  const { values, setValues, submitCount } = formik ?? {};

  // kula validates on blur unless told otherwise, so a never-focused field
  // showed nothing and the surface had to fall back to a toast.
  const attempted = submitCount ?? 0;

  // A surface-supplied list wins: it exists precisely to restrict senders.
  const senders = useMemo(
    () => (defaultAllowedFromUsers ?? fromUsers ?? []) as TemplateEmailUser[],
    [defaultAllowedFromUsers, fromUsers]
  );

  const fromOptions = useMemo(() => toSelectOptions(senders), [senders]);
  // From searches the account too, the way the interview emails do. The
  // searched senders feed `resolveUser` so a picked sender still resolves.
  const { fromSelectProps, searchedSenders } = useSenderAutocomplete(
    fromOptions,
    'email'
  );
  // CC/BCC searches the account server-side; the redux user list is a page, so
  // filtering it never offered everyone (ENG-3766). Keyed by email here.
  const knownCcBccOptions = useMemo(
    () => toSelectOptions(allUsers as TemplateEmailUser[]),
    [allUsers]
  );
  const { initialOptions: ccBccOptions, loadOptions: loadCcBcc } =
    useCcBccUserOptions(knownCcBccOptions, 'email');
  const ccBccSelectProps = useMemo(
    () => asyncSelectProps(loadCcBcc),
    [loadCcBcc]
  );

  const resolveUser = useCallback(
    (email: string) =>
      [
        ...senders,
        ...(searchedSenders as TemplateEmailUser[]),
        ...((allUsers ?? []) as TemplateEmailUser[]),
      ].find((user) => user?.email === email),
    [senders, searchedSenders, allUsers]
  );

  // Seed only — binding `value` straight to Formik would spin the form.
  // `template` is adopted below rather than here: kula drops a value its
  // options cannot resolve, and they load after this runs.
  const [value, setValue] = useState(() =>
    toEmailValue({
      ...values,
      subject: values?.[subjectFieldName],
      body: values?.[bodyFieldName],
    })
  );
  const valueRef = useRef(value);

  // Subject/body land on the names this instance owns, not the defaults.
  const toFormPatch = useCallback(
    (next: EmailCompositionValue) => {
      const { subject, body, ...rest } = fromEmailValue(next, resolveUser);
      return {
        ...rest,
        [subjectFieldName]: subject,
        [bodyFieldName]: body,
      };
    },
    [bodyFieldName, resolveUser, subjectFieldName]
  );

  // Present only when the surface hands the picker over to the email card.
  const showTemplate = !!onTemplateChange;
  const templateOptions = useMemo(
    () => toTemplateOptions(templates ?? []),
    [templates]
  );

  const handleChange = useCallback(
    (next: EmailCompositionValue) => {
      const prev = valueRef.current;
      // Deferred: setting parent state inside kula's lifecycle makes Formik flush mid-render.
      if (showTemplate && next.template !== prev.template) {
        const picked = (templates ?? []).find(
          (row) => String(row?.id) === next.template
        );
        queueMicrotask(() => onTemplateChange?.(picked ?? null));
      }

      if (isSameEmailValue(prev, next)) {
        // Still store the row selection so the field reflects the click.
        if (next.template !== prev.template) {
          valueRef.current = next;
          setValue(next);
        }
        return;
      }

      valueRef.current = next;
      setValue(next);
      setValues((current: Record<string, unknown>) => ({
        ...current,
        ...toFormPatch(next),
      }));
    },
    [onTemplateChange, setValues, showTemplate, templates, toFormPatch]
  );

  // Adopt a sender only once the list confirms it is selectable.
  const wantedFrom = resolveSelectableFrom(values?.from, senders, defaultFrom);
  const seededFrom = useRef(false);
  useEffect(() => {
    if (seededFrom.current || !wantedFrom) return;
    if (valueRef.current.from === wantedFrom) return;
    seededFrom.current = true;

    const seeded = { ...valueRef.current, from: wantedFrom };
    valueRef.current = seeded;
    setValue(seeded);
    setValues((current: Record<string, unknown>) => ({
      ...current,
      ...toFormPatch(seeded),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedFrom]);

  // Keyed so a re-render never re-applies the template over the user's edits.
  const appliedTemplate = useRef(templateKey(selectedTemplate));

  // Show the selection as soon as the loaded options can resolve it.
  // Keyed on the selection so a second activity loading into a still-mounted
  // composer adopts its own template instead of keeping the first one's.
  const adoptedTemplate = useRef<string | null>(null);
  useEffect(() => {
    const key = templateKey(selectedTemplate);
    if (adoptedTemplate.current === key || !key) return;
    if (!templateOptions.some((option) => option.value === key)) return;
    adoptedTemplate.current = key;

    const adopted = { ...valueRef.current, template: key };
    valueRef.current = adopted;
    setValue(adopted);
  }, [selectedTemplate, templateOptions]);
  useEffect(() => {
    const key = templateKey(selectedTemplate);
    if (key === appliedTemplate.current) return;
    if (!selectedTemplate) {
      appliedTemplate.current = key;
      return;
    }
    // A selection seeded from a saved activity is an id with no content behind
    // it; applying it would blank the subject and body it is meant to label.
    if (
      selectedTemplate.subject === undefined &&
      selectedTemplate.body === undefined
    ) {
      // A cleared picker carries no id, so release the applied key — otherwise
      // re-picking the same template is a no-op. A seeded id keeps the key it
      // has, so the real content still applies when the user picks it.
      if (!key) appliedTemplate.current = key;
      return;
    }
    appliedTemplate.current = key;

    const applied = {
      ...valueRef.current,
      template: key || null,
      subject: selectedTemplate.subject ?? '',
      content: selectedTemplate.body ?? '',
    };
    valueRef.current = applied;
    setValue(applied);
    setValues((current: Record<string, unknown>) => ({
      ...current,
      [subjectFieldName]: applied.subject,
      [bodyFieldName]: applied.content,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  const attachments: TemplateEmailAttachment[] = useMemo(
    () => values?.attachments ?? [],
    [values?.attachments]
  );
  const editorAttachments = useMemo(
    () => toEditorAttachments(attachments, nonRemovableAttachments.length),
    [attachments, nonRemovableAttachments.length]
  );

  const handleAttachFiles = useCallback(
    (files: File[]) =>
      setValues((current: Record<string, unknown>) => ({
        ...current,
        attachments: [...attachments, ...files],
      })),
    [attachments, setValues]
  );
  const attachmentGuard = useAttachmentGuard(
    editorAttachments,
    handleAttachFiles
  );

  const handleRemoveAttachment = useCallback(
    (key: string) => {
      const index = attachments.findIndex(
        (item, i) => attachmentKey(item, i) === key
      );
      // Pinned rows keep their chip; refuse the removal rather than dropping it.
      if (index < 0 || index < nonRemovableAttachments.length) return;

      onAttachmentRemove?.(attachments[index]);
      setValues((current: Record<string, unknown>) => ({
        ...current,
        attachments: attachments.filter((_item, i) => i !== index),
      }));
    },
    [attachments, nonRemovableAttachments.length, onAttachmentRemove, setValues]
  );

  const toolbarFeatures = useMemo(
    () => ({
      image: true,
      attachment: true,
      fontSize: enableFontControls,
      fontFamily: enableFontControls,
      ...toolbarFeatureOverrides,
    }),
    [enableFontControls, toolbarFeatureOverrides]
  );

  // survey.link is insert-once per body — only one survey goes to a candidate.
  const editorVariables = useMemo(
    () => ({
      vars: variables,
      disabledKeys: hasSurveyPlaceholder(value.content)
        ? SURVEY_LINK_KEYS
        : EMPTY_KEYS,
    }),
    [variables, value.content]
  );

  // kula hands over the tiptap instance once the editor mounts.
  const [editorInstance, setEditorInstance] = useState<unknown>(null);

  // Memoised: a fresh object each render rebuilds the subject input.
  const subjectInputProps = useMemo(
    () => (subjectMaxLength ? { maxLength: subjectMaxLength } : undefined),
    [subjectMaxLength]
  );

  // Flag derived apart from the props object so a keystroke doesn't hand the
  // template `Select` a new identity.
  const templateEdited = useMemo(() => {
    const selected = templateOptions?.find(
      (option) => option.value === value.template
    );
    return isTemplateEdited(value, selected);
  }, [templateOptions, value]);

  const mergedTemplateSelectProps = useMemo(
    () => ({
      ...(loadTemplates
        ? {
            variant: 'async' as const,
            defaultOptions: true,
            loadOptions: async (input: string) =>
              toTemplateOptions(await loadTemplates(input)),
          }
        : {}),
      ...customTemplateSelectProps(templateEdited),
    }),
    [loadTemplates, templateEdited]
  );

  return (
    <Email
      showFrom={showFrom}
      showTo={false}
      showSubject
      showEditor
      enableCcBcc
      showTemplate={showTemplate}
      // The `selectedTemplate` effect applies subject AND body together;
      // letting kula set the body too would apply it twice.
      applyTemplateBodyToEditor={false}
      templateOptions={templateOptions}
      templateSearchable
      templateSearchPlaceholder="Search templates"
      // Keeps server-side search, so the picker still reaches templates beyond
      // the page the surface preloaded.
      templateSelectProps={mergedTemplateSelectProps}
      disabled={disabled}
      submitAttempt={attempted}
      validateOnMount={attempted > 0}
      value={value}
      onChange={handleChange}
      fromOptions={fromOptions}
      fromSelectProps={fromSelectProps}
      ccBccOptions={ccBccOptions}
      ccSelectProps={ccBccSelectProps}
      bccSelectProps={ccBccSelectProps}
      placeholders={{ from: 'Select from', subject: 'Enter subject' }}
      subjectVariableInputProps={subjectInputProps}
      editorProps={{
        placeholder: 'Start typing...',
        // Pasted/inserted images POST to `/user_image_uploads` and the editor keeps the
        // returned URL; without it the body stores a base64 data URL.
        uploadInlineImage: uploadUserInlineImage,
        hideToolbar,
        extraExtensions,
        toolbarStartSlot: renderToolbarStartSlot?.(editorInstance),
        onEditorReady: setEditorInstance,
        toolbarFeatures,
        variable: editorVariables,
        attachments: editorAttachments,
        ...attachmentGuard,
        onRemoveAttachment: handleRemoveAttachment,
      }}
    />
  );
};
